import React, { useEffect, useState } from 'react'
import { getFloorsIntervals } from 'utils/bpData'
import { isNotNilOrEmpty } from 'utils/ramda'
import styled from 'styled-components'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined'
import MenuItem from '@mui/material/MenuItem'
import Menu from '@mui/material/Menu'
import Tooltip from '@mui/material/Tooltip'
import { useSelector } from 'react-redux'
import { pathOr } from 'ramda'
import { selectBpUnits } from 'ducks/units/selectors'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import { redirect } from 'utils/paths'
import QrCodePreviewModal from 'features/qrLanding/QrCodePreviewModal'
import UnitEpcBadge from 'features/bpDetails/Components/units/UnitEpcBadge'
import { useEpcSync } from 'features/bpDetails/Components/units/useEpcSync'
import { InlineTextCell, InlineFloorsCell } from 'features/bpDetails/Components/units/InlineUnitCells'

const isGeneratedUprnName = value => /^UPRN\s+\d+/i.test(String(value || '').trim())

const extractUnitNameFromAddress = value => {
  const address = String(value || '').trim()
  if (!address || isGeneratedUprnName(address)) return ''

  const parts = address
    .split(',')
    .map(part => part.trim())
    .filter(Boolean)

  if (!parts.length) return ''

  const first = parts[0]
  const looksLikeUnit = /\b(flat|apartment|apt|unit|suite|room|studio|plot|floor|level|office|shop)\b/i.test(first)

  if (looksLikeUnit) {
    return parts[1] ? `${first}, ${parts[1]}` : first
  }

  return ''
}

// Units created from OS data are named "3.10, The Wool House, Hexham…" —
// show the identifier as the name and the rest as a muted address line.
const splitUnitNameAndAddress = value => {
  const name = String(value || '').trim()
  const commaIndex = name.indexOf(',')
  if (commaIndex === -1) return [name, '']
  return [name.slice(0, commaIndex).trim(), name.slice(commaIndex + 1).trim()]
}

const DetailsUnitTableItem = ({
  unit,
  unitsCount,
  openEditModal,
  openDeleteModal,
  closeDropdownTrigger,
  isSoleUnit,
  isSoleResidential,
  hasFullAccess,
  redirectPath,
  editMode = false
}) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [qrOpen, setQrOpen] = useState(false)
  const dropdownOpen = Boolean(anchorEl)
  const bpUnits = useSelector(selectBpUnits)
  const bp = useSelector(getSelectedBp)
  const { epc, checking, check } = useEpcSync({ bpId: bp?.id, unitId: unit.id, unit })
  const displayName = isGeneratedUprnName(unit.name)
    ? extractUnitNameFromAddress(unit.addressData?.address || unit.address_data?.address) || unit.name
    : unit.name
  const [unitLabel, unitAddress] = splitUnitNameAndAddress(displayName)

  const floorNumbers = floors => {
    const sorted = floors
      .map(num => num === 'lower_ground' ? -0.5 : Number(num))
      .sort((a, b) => b - a)

    return getFloorsIntervals(sorted)
  }

  useEffect(() => {
    setAnchorEl(null)
  }, [closeDropdownTrigger])

  const openMenu = e => {
    e.stopPropagation()
    e.preventDefault()
    setAnchorEl(e.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleItemClick = () => {
    if (editMode) return
    if (!anchorEl) {
      redirect(`${redirectPath}/${unit.id}`)
    }
  }

  return (
    <TableRow onClick={handleItemClick} $editing={editMode}>
      {editMode ? (
        <>
          <td>
            <InlineTextCell bpId={bp?.id} unit={unit} field='name' placeholder='Unit name' minWidth='180px' />
          </td>
          <td>
            <InlineTextCell bpId={bp?.id} unit={unit} field='uprn' placeholder='UPRN' inputMode='numeric' minWidth='140px' />
          </td>
          <td>
            <InlineTextCell bpId={bp?.id} unit={unit} field='plotNumber' placeholder='Plot' minWidth='90px' />
          </td>
          <td>
            <InlineFloorsCell bpId={bp?.id} bp={bp} unit={unit} minWidth='140px' />
          </td>
          <td>
            <InlineTextCell bpId={bp?.id} unit={unit} field='area' placeholder='sqm' inputMode='decimal' minWidth='90px' />
          </td>
        </>
      ) : (
        <>
          <td>
            <UnitNameCell>
              <span>{unitLabel || displayName}</span>
              {unitAddress && <UnitAddress>{unitAddress}</UnitAddress>}
            </UnitNameCell>
          </td>
          <td>{unit.uprn || 'Not yet allocated'}</td>
          <td>{unit.plotNumber || '---'}</td>
          <td>
            {isNotNilOrEmpty(unit.floorNumbers) ? (
              <FloorCell>
                {floorNumbers(unit.floorNumbers).join(', ')}
                {unit.floorConfidence === 'medium' && (
                  <Tooltip title='Suggested from the unit number in the public address data. Open the unit to confirm or change it.'>
                    <SuggestedFloorMark aria-label='Suggested floor'>?</SuggestedFloorMark>
                  </Tooltip>
                )}
              </FloorCell>
            ) : (
              '---'
            )}
          </td>
          <td>{unit.area || '---'}</td>
        </>
      )}
      <td><UnitEpcBadge unit={{ ...unit, ...epc }} compact onCheck={check} checking={checking} /></td>
      <td>{
        pathOr(false, ['owners', 0], unit)
          ? `${unit.owners[0].firstName} ${unit.owners[0].lastName}`
          : 'Unassigned'
      }</td>
      <td>
        {hasFullAccess && (
          <IconWrapper>
            <Tooltip title="Show QR code for this unit">
              <QrButton
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); setQrOpen(true) }}
                aria-label="Show QR code"
              >
                <QrCode2OutlinedIcon style={{ fontSize: 18 }} />
              </QrButton>
            </Tooltip>
            <MoreIcon onClick={openMenu} />
              <Menu
                keepMounted
                anchorEl={anchorEl}
                style={{
                  marginTop: '10px'
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center'
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center'
                }}
                open={dropdownOpen}
                onClose={handleClose}
              >
                <MenuItem
                  onClick={openEditModal(unit)}
                  disabled={
                    isSoleResidential ||
                    (unitsCount === 1 && isSoleUnit)
                  }
                >
                  Edit
                </MenuItem>
                <MenuItem
                  disabled={
                    bpUnits.length <= 1 ||
                    isSoleResidential
                  }
                  onClick={openDeleteModal(unit)}
                >
                  Remove Unit
                </MenuItem>
              </Menu>
          </IconWrapper>
        )}
      </td>
      {qrOpen && (
        <QrCodePreviewModal
          open={qrOpen}
          onClose={() => setQrOpen(false)}
          kind="unit"
          bpId={bp?.id}
          unitId={unit.id}
          name={displayName}
        />
      )}
    </TableRow>
  )
}

export default DetailsUnitTableItem

const MoreIcon = styled(MoreVertIcon)`
  color: ${({ theme }) => theme.colors.grey[600]};
  cursor: pointer;
  font-size: 20px !important;
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  padding: 5px;
`

const QrButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  background: #fff;
  color: ${({ theme }) => theme.colors.haiti};
  border-radius: 6px;
  padding: 4px 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover { background: #f1f5f9; }
`

const FloorCell = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

const SuggestedFloorMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fef3c7;
  color: #b45309;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  cursor: help;
`

const UnitNameCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const UnitAddress = styled.span`
  color: ${({ theme }) => theme.colors.grey[600]};
  font-size: 12px;
  font-weight: 400;
  line-height: 1.35;
`

const TableRow = styled.tr`
  ${({ $editing }) => $editing && `
    cursor: default !important;

    &:hover {
      background: transparent !important;
    }

    td {
      padding-top: 8px;
      padding-bottom: 8px;
    }
  `}
`
